import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const CRMAuthContext = createContext(null);
export const crmAPI = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/crm`, withCredentials: true });
let csrf = sessionStorage.getItem('crm_csrf') || '';
let refreshPromise;
const storeIdentity = data => {
  csrf = data.csrf_token;
  sessionStorage.setItem('crm_csrf', csrf);
  return data;
};
export const apiError = error => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return 'Lütfen alanları kontrol edin. Şifre en az 12 karakter, büyük/küçük harf ve rakam içermelidir.';
  return 'İşlem tamamlanamadı. Lütfen tekrar deneyin.';
};
crmAPI.interceptors.request.use(config => {
  if (!['get', 'head', 'options'].includes(config.method)) config.headers['X-CSRF-Token'] = csrf;
  return config;
});
crmAPI.interceptors.response.use(response => response, async error => {
  const config = error.config;
  const isSessionEndpoint = ['/auth/login', '/auth/refresh', '/auth/logout'].includes(config?.url);
  if (error.response?.status === 401 && !isSessionEndpoint && !config?._retried && csrf) {
    config._retried = true;
    try {
      if (!refreshPromise) refreshPromise = crmAPI.post('/auth/refresh').then(r => storeIdentity(r.data)).finally(() => { refreshPromise = null; });
      await refreshPromise;
      return crmAPI(config);
    } catch (_) { /* The expired session is handled below. */ }
  }
  if (error.response?.status === 401 && config?.url !== '/auth/login') window.dispatchEvent(new Event('crm-session-expired'));
  return Promise.reject(error);
});

export const CRMAuthProvider = ({ children }) => {
  const generation = useRef(0);
  const endingSession = useRef(false);
  const [identity, setIdentity] = useState(null);
  const [navigation, setNavigation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const reset = useCallback(() => {
    generation.current += 1;
    setIdentity(null); setNavigation([]); csrf = ''; sessionStorage.removeItem('crm_csrf');
  }, []);
  const reload = useCallback(async () => {
    if (endingSession.current) return;
    const version = generation.current;
    const response = await crmAPI.get('/auth/me');
    const menu = response.data.mfa_enrollment_required ? { data: [] } : await crmAPI.get('/navigation');
    if (version !== generation.current || endingSession.current) return;
    setIdentity(storeIdentity(response.data)); setNavigation(menu.data);
  }, []);
  useEffect(() => {
    localStorage.removeItem('crm_token'); localStorage.removeItem('crm_user');
    const onExpired = () => { const hadSession = Boolean(csrf); reset(); setExpired(hadSession); };
    window.addEventListener('crm-session-expired', onExpired);
    reload().catch(() => reset()).finally(() => setLoading(false));
    return () => window.removeEventListener('crm-session-expired', onExpired);
  }, [reload, reset]);
  useEffect(() => {
    const onFocus = () => { if (identity) reload().catch(() => {}); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [identity, reload]);
  const login = async values => {
    generation.current += 1;
    const { data } = await crmAPI.post('/auth/login', values);
    if (data.mfa_required || data.mfa_setup_required) {
      return data;
    }
    // Switching staff identities must not retain a previous legacy Manager JWT.
    localStorage.removeItem('token'); localStorage.removeItem('user');
    setIdentity(storeIdentity(data)); setExpired(false);
    setNavigation(data.mfa_enrollment_required ? [] : (await crmAPI.get('/navigation')).data);
    return data;
  };
  const logout = async () => {
    if (endingSession.current) return;
    endingSession.current = true; generation.current += 1;
    try {
      try { await crmAPI.post('/auth/logout'); }
      catch (error) { if (error.response?.status !== 401) throw error; }
      reset(); setExpired(false);
    } finally { endingSession.current = false; }
  };
  return <CRMAuthContext.Provider value={{ ...identity, user: identity?.user, navigation, loading, expired, login, logout, reload, reset }}>{children}</CRMAuthContext.Provider>;
};
export const useCRMAuth = () => useContext(CRMAuthContext);